import React, { useEffect, useMemo, useState } from 'react';
import { chileRegions, normalizeChileRegion, getChileRegionShortLabel } from '../../config/chileRegions';
import { useUIColors } from '../../hooks/useTheme';
import ChartOptionsMenu from './ChartOptionsMenu';
import ChartPresetsMenu, {
  filterChartPresets,
  REGION_CHART_PRESETS,
} from './ChartPresetsMenu';
import ChartSeriesLegend from './ChartSeriesLegend';
import StackedChartHoverTooltip from './StackedChartHoverTooltip';
import {
  aggregateByAmountRanges,
  aggregateByGallonPriceRanges,
  aggregateSaleRateRows,
  buildAmountRangesFromConfig,
  buildGallonPriceRangesFromConfig,
  collapseItemsToQuotations,
  computeSaleRateFromStatuses,
  DEFAULT_AMOUNT_RANGE_CONFIG,
  DEFAULT_GALLON_PRICE_RANGE_CONFIG,
  discoverDimensionOptions,
  filterItemsByDimensions,
  formatChartMetricValue,
  getChartUniqueKey,
  isChartRateMetric,
  isFilterVisible,
  normalizeAmountRangeConfig,
  normalizeGallonPriceRangeConfig,
  resolveDimensionValue,
  resolveItemWeight,
  toggleVisibilityKey,
  visibilityMapFromOptions,
} from './chartFilterUtils';

const CHART_PLOT_HEIGHT_PX = 234; // 180 * 1.3

const SEGMENT_STACK_ORDER = ['B2G', 'B2B', 'B2C', 'B2B2C', 'Sin Asignar'];

const FALLBACK_PALETTE = [
  '#0066cc',
  '#00a651',
  '#f39c12',
  '#9b59b6',
  '#e74c3c',
  '#3498db',
  '#1abc9c',
  '#e67e22',
  '#34495e',
  '#7f8c8d',
];

export const normalizeCommercialSegment = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return 'Sin Asignar';
  const compact = raw.toUpperCase().replace(/\s+/g, '');
  if (compact === 'SINASIGNAR' || compact === 'SIN_ASIGNAR') return 'Sin Asignar';
  if (compact === 'B2B2C') return 'B2B2C';
  if (compact === 'B2B' || compact === 'B2C' || compact === 'B2G') return compact;
  if (SEGMENT_STACK_ORDER.includes(raw)) return raw;
  return 'Sin Asignar';
};

export const SEGMENT_STACK_DIMENSION = {
  key: 'segment',
  label: 'Segmento',
  order: SEGMENT_STACK_ORDER,
  normalize: normalizeCommercialSegment,
};

export const REGION_FILTER_DIMENSION = {
  key: 'region',
  label: 'Región',
  order: chileRegions.map((region) => region.value).concat(['Sin región']),
  normalize: (value) => normalizeChileRegion(value) || 'Sin región',
  fallback: 'Sin región',
};

export const PRODUCT_LINE_DIMENSION = {
  key: 'productLine',
  label: 'Línea',
  order: ['T-COAT', 'Upselling', 'Sin ítems'],
  colors: {
    'T-COAT': '#0066cc',
    Upselling: '#ea580c',
    'Sin ítems': '#9ca3af',
  },
  fallback: 'Upselling',
};

/** Individual product name (for precio/galón filters). */
export const PRODUCT_NAME_DIMENSION = {
  key: 'productName',
  label: 'Producto',
  fallback: 'Sin producto',
};

export const TCOAT_PRODUCT_DIMENSION = {
  key: 'tcoatProduct',
  label: 'Producto T-COAT',
  fallback: 'No T-COAT',
};

export const REGION_ONLY_AXIS_MODES = [{ key: 'region', label: 'Región' }];

export const REGION_CHART_AXIS_MODES = [
  { key: 'region', label: 'Región' },
  { key: 'amount', label: 'Monto' },
  { key: 'gallonPrice', label: 'Precio/galón' },
];

const GALLON_PRICE_RANGE_FIELDS = [
  { key: 'minThousands', label: 'Desde (K)', min: 0 },
  { key: 'maxThousands', label: 'Hasta (K)', min: 0 },
  { key: 'stepThousands', label: 'Tamaño (K)', min: 0.5 },
];

const AMOUNT_RANGE_FIELDS = [
  { key: 'minMillions', label: 'Desde (M)', min: 0 },
  { key: 'maxMillions', label: 'Hasta (M)', min: 0 },
  { key: 'stepMillions', label: 'Tamaño (M)', min: 0.1 },
];

const buildColorMap = (keys, explicitColors, palette) => {
  const map = { ...(explicitColors || {}) };
  let paletteIndex = 0;
  keys.forEach((key) => {
    if (!map[key]) {
      map[key] = palette[paletteIndex % palette.length];
      paletteIndex += 1;
    }
  });
  return map;
};

/**
 * Stacked bar chart by Chile region or amount ranges × selectable stack dimension.
 */
const RegionSegmentStackedChart = ({
  items = [],
  title = 'Distribución de cotizaciones',
  totalLabel = 'registros',
  emptyMessage = 'Sin datos para mostrar.',
  stackDimensions = [SEGMENT_STACK_DIMENSION],
  defaultStackKey,
  filterOnlyDimensions = [],
  valueMetrics = [],
  defaultMetricKey = 'count',
  axisModes = REGION_CHART_AXIS_MODES,
  defaultAxisKey = 'region',
  chartPresets = REGION_CHART_PRESETS,
}) => {
  const ui = useUIColors();
  const theme = ui.theme || {};
  const chartColors = theme.chart || {};
  const [hoverInfo, setHoverInfo] = useState(null);
  const palette = useMemo(
    () =>
      [
        chartColors.primary,
        chartColors.tertiary,
        chartColors.quaternary,
        chartColors.quinary,
        chartColors.secondary,
        chartColors.senary,
        ...FALLBACK_PALETTE,
      ].filter(Boolean),
    [chartColors]
  );

  const modes = Array.isArray(axisModes) && axisModes.length ? axisModes : REGION_CHART_AXIS_MODES;
  const dimensions = stackDimensions.length ? stackDimensions : [SEGMENT_STACK_DIMENSION];
  const metrics = Array.isArray(valueMetrics) ? valueMetrics : [];

  const initialKey =
    defaultStackKey && dimensions.some((dimension) => dimension.key === defaultStackKey)
      ? defaultStackKey
      : dimensions[0].key;
  const initialMetric =
    defaultMetricKey && metrics.some((metric) => metric.key === defaultMetricKey)
      ? defaultMetricKey
      : metrics[0]?.key || 'count';
  const initialAxis =
    defaultAxisKey && modes.some((mode) => mode.key === defaultAxisKey)
      ? defaultAxisKey
      : modes[0].key;

  const [stackKey, setStackKey] = useState(initialKey);
  const [metricKey, setMetricKey] = useState(initialMetric);
  const [axisKey, setAxisKey] = useState(initialAxis);
  const [dimensionVisibility, setDimensionVisibility] = useState({});
  const [rangeVisibility, setRangeVisibility] = useState({});
  const [amountRangeDraft, setAmountRangeDraft] = useState(DEFAULT_AMOUNT_RANGE_CONFIG);
  const [amountRangeConfig, setAmountRangeConfig] = useState(DEFAULT_AMOUNT_RANGE_CONFIG);
  const [gallonPriceDraft, setGallonPriceDraft] = useState(DEFAULT_GALLON_PRICE_RANGE_CONFIG);
  const [gallonPriceConfig, setGallonPriceConfig] = useState(DEFAULT_GALLON_PRICE_RANGE_CONFIG);

  const isGallonPriceAxis = axisKey === 'gallonPrice';

  const filterDimensions = useMemo(() => {
    const hasRegion = dimensions.some((dimension) => dimension.key === 'region');
    const base = hasRegion ? [...dimensions] : [...dimensions, REGION_FILTER_DIMENSION];
    const extras = (Array.isArray(filterOnlyDimensions) ? filterOnlyDimensions : []).filter(
      (dimension) => !base.some((entry) => entry.key === dimension.key)
    );
    if (isGallonPriceAxis && !base.some((entry) => entry.key === PRODUCT_NAME_DIMENSION.key)) {
      return [...base, PRODUCT_NAME_DIMENSION, ...extras.filter((d) => d.key !== PRODUCT_NAME_DIMENSION.key)];
    }
    return [...base, ...extras];
  }, [dimensions, filterOnlyDimensions, isGallonPriceAxis]);

  useEffect(() => {
    if (!dimensions.some((dimension) => dimension.key === stackKey)) {
      setStackKey(dimensions[0].key);
    }
  }, [dimensions, stackKey]);

  useEffect(() => {
    if (!metrics.length) return;
    if (!metrics.some((metric) => metric.key === metricKey)) {
      setMetricKey(metrics[0].key);
    }
  }, [metrics, metricKey]);

  const modeKeys = modes.map((mode) => mode.key).join('|');
  useEffect(() => {
    if (!modes.length) return;
    if (!modes.some((mode) => mode.key === axisKey)) {
      setAxisKey(modes[0].key);
    }
    // modeKeys captures axis-mode identity without depending on array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeKeys, axisKey]);

  const activeDimension = dimensions.find((dimension) => dimension.key === stackKey) || dimensions[0];
  const activeMetric = metrics.find((metric) => metric.key === metricKey) || null;
  const activeAxis = modes.find((mode) => mode.key === axisKey) || modes[0];
  const resolvedMetricKey = activeMetric?.key || 'count';
  const resolvedTotalLabel = activeMetric?.totalLabel || totalLabel;
  const isAmountAxis = activeAxis?.key === 'amount';
  const isRangeAxis = isAmountAxis || isGallonPriceAxis;

  const defaultSegmentColors = useMemo(
    () => ({
      B2G: chartColors.primary || ui.interactive,
      B2B: chartColors.tertiary || ui.success,
      B2C: chartColors.quaternary || ui.warning,
      B2B2C: chartColors.quinary || '#9b59b6',
      'Sin Asignar': chartColors.secondary || ui.danger,
    }),
    [chartColors, ui.interactive, ui.success, ui.warning, ui.danger]
  );

  const filteredItems = useMemo(
    () => filterItemsByDimensions(items, filterDimensions, dimensionVisibility),
    [items, filterDimensions, dimensionVisibility]
  );

  const amountRanges = useMemo(
    () => buildAmountRangesFromConfig(amountRangeConfig),
    [amountRangeConfig]
  );

  const gallonPriceRanges = useMemo(
    () => buildGallonPriceRangesFromConfig(gallonPriceConfig),
    [gallonPriceConfig]
  );

  const { rows, seriesPresent } = useMemo(() => {
    const rateMetric = isChartRateMetric(resolvedMetricKey);
    const rateSeriesLabel = activeMetric?.label || 'Tasa';

    if (rateMetric && isAmountAxis) {
      const quotationRows = collapseItemsToQuotations(filteredItems);
      const buckets = (amountRanges || []).map((range) => ({
        key: range.key,
        label: range.label,
        items: quotationRows.filter((row) => {
          const amount = Number(row.amount || 0);
          return amount >= range.min && amount < range.max;
        }),
      }));
      return aggregateSaleRateRows({
        buckets,
        metricKey: resolvedMetricKey,
        seriesLabel: rateSeriesLabel,
      });
    }

    if (rateMetric && isGallonPriceAxis) {
      const buckets = (gallonPriceRanges || []).map((range) => ({
        key: range.key,
        label: range.label,
        items: filteredItems.filter((item) => {
          const price = Number(item?.pricePerGallon || 0);
          return price > 0 && price >= range.min && price < range.max;
        }),
      }));
      return aggregateSaleRateRows({
        buckets,
        metricKey: resolvedMetricKey,
        seriesLabel: rateSeriesLabel,
      });
    }

    if (rateMetric) {
      const byRegion = {};
      filteredItems.forEach((item) => {
        const region = normalizeChileRegion(item?.region) || 'Sin región';
        if (!byRegion[region]) byRegion[region] = [];
        byRegion[region].push(item);
      });
      const knownValues = new Set(chileRegions.map((region) => region.value));
      const buckets = [];
      chileRegions.forEach((region) => {
        const items = byRegion[region.value];
        if (!items?.length) return;
        buckets.push({
          key: region.value,
          label: region.value,
          shortLabel: region.shortLabel || region.value,
          items,
        });
      });
      Object.entries(byRegion).forEach(([region, items]) => {
        if (knownValues.has(region) || region === 'Sin región') return;
        buckets.push({
          key: region,
          label: region,
          shortLabel: getChileRegionShortLabel(region),
          items,
        });
      });
      if (byRegion['Sin región']?.length) {
        buckets.push({
          key: 'Sin región',
          label: 'Sin región',
          shortLabel: 'S/R',
          items: byRegion['Sin región'],
        });
      }
      return aggregateSaleRateRows({
        buckets,
        metricKey: resolvedMetricKey,
        seriesLabel: rateSeriesLabel,
      });
    }

    if (isAmountAxis) {
      const quotationRows = collapseItemsToQuotations(filteredItems);
      return aggregateByAmountRanges({
        quotationRows,
        ranges: amountRanges,
        activeDimension,
        metricKey: resolvedMetricKey,
      });
    }

    if (isGallonPriceAxis) {
      return aggregateByGallonPriceRanges({
        items: filteredItems,
        ranges: gallonPriceRanges,
        activeDimension,
        metricKey: resolvedMetricKey,
      });
    }

    const byRegion = {};
    const discovered = new Set();
    const countUnique = resolvedMetricKey === 'count';

    filteredItems.forEach((item, index) => {
      const region = normalizeChileRegion(item?.region) || 'Sin región';
      const series = resolveDimensionValue(item, activeDimension);
      if (!byRegion[region]) {
        byRegion[region] = countUnique
          ? { totalIds: new Set(), bySeriesIds: {} }
          : { total: 0, bySeries: {} };
      }

      if (countUnique) {
        const uid = getChartUniqueKey(item) || `line-${index}`;
        byRegion[region].totalIds.add(uid);
        if (!byRegion[region].bySeriesIds[series]) byRegion[region].bySeriesIds[series] = new Set();
        byRegion[region].bySeriesIds[series].add(uid);
        discovered.add(series);
        return;
      }

      const weight = resolveItemWeight(item, resolvedMetricKey);
      if (weight <= 0) return;
      byRegion[region].total += weight;
      byRegion[region].bySeries[series] = (byRegion[region].bySeries[series] || 0) + weight;
      discovered.add(series);
    });

    const toStats = (entry) => {
      if (!entry) return { total: 0, bySeries: {} };
      if (!countUnique) return entry;
      const bySeries = {};
      Object.entries(entry.bySeriesIds || {}).forEach(([key, set]) => {
        bySeries[key] = set.size;
      });
      return { total: entry.totalIds.size, bySeries };
    };

    const preferredOrder = Array.isArray(activeDimension.order) ? activeDimension.order : [];
    const seriesOrder = [
      ...preferredOrder.filter((key) => discovered.has(key)),
      ...[...discovered].filter((key) => !preferredOrder.includes(key)).sort((a, b) => a.localeCompare(b, 'es')),
    ];

    const knownValues = new Set(chileRegions.map((region) => region.value));
    const ordered = chileRegions
      .map((region) => {
        const stats = toStats(byRegion[region.value]);
        return {
          key: region.value,
          label: region.value,
          shortLabel: region.shortLabel || region.value,
          total: stats.total,
          bySeries: stats.bySeries,
        };
      })
      .filter((row) => row.total > 0);

    Object.entries(byRegion).forEach(([region, entry]) => {
      if (!knownValues.has(region) && region !== 'Sin región') {
        const stats = toStats(entry);
        ordered.push({
          key: region,
          label: region,
          shortLabel: getChileRegionShortLabel(region),
          total: stats.total,
          bySeries: stats.bySeries,
        });
      }
    });

    if (byRegion['Sin región']) {
      const stats = toStats(byRegion['Sin región']);
      ordered.push({
        key: 'Sin región',
        label: 'Sin región',
        shortLabel: 'S/R',
        total: stats.total,
        bySeries: stats.bySeries,
      });
    }

    return { rows: ordered, seriesPresent: seriesOrder };
  }, [
    filteredItems,
    activeDimension,
    activeMetric,
    resolvedMetricKey,
    isAmountAxis,
    isGallonPriceAxis,
    amountRanges,
    gallonPriceRanges,
  ]);

  const visibleRows = useMemo(() => {
    if (!isRangeAxis) return rows;
    return rows.filter((row) => isFilterVisible(rangeVisibility, row.key));
  }, [rows, isRangeAxis, rangeVisibility]);

  const seriesColors = useMemo(() => {
    const rateColors = {
      'Tasa de venta': '#0d9488',
      'Tasa de venta ajustada': '#2563eb',
      Tasa: '#0d9488',
    };
    const explicit =
      activeDimension.key === 'segment'
        ? { ...defaultSegmentColors, ...(activeDimension.colors || {}), ...rateColors }
        : { ...(activeDimension.colors || {}), ...rateColors };
    return buildColorMap(seriesPresent, explicit, palette);
  }, [activeDimension, defaultSegmentColors, seriesPresent, palette]);

  const total = useMemo(() => {
    if (isChartRateMetric(resolvedMetricKey)) {
      const allStatus = new Map();
      filteredItems.forEach((item, index) => {
        const uid = getChartUniqueKey(item) || `line-${index}`;
        if (allStatus.has(uid)) return;
        allStatus.set(uid, String(item?.status || '').trim() || 'Sin estado');
      });
      return computeSaleRateFromStatuses(allStatus, resolvedMetricKey === 'adjustedSaleRate');
    }
    return visibleRows.reduce((sum, row) => sum + row.total, 0);
  }, [visibleRows, resolvedMetricKey, filteredItems]);
  const maxCount = isChartRateMetric(resolvedMetricKey)
    ? 100
    : Math.max(...visibleRows.map((item) => item.total), 1);
  const hasAnyData = isChartRateMetric(resolvedMetricKey)
    ? filteredItems.length > 0
    : visibleRows.some((row) => row.total > 0);

  const border = ui.border;
  const headerBg = ui.header;
  const text = ui.text;
  const muted = ui.muted;
  const panel = ui.panel;
  const chartTitle = `${title} por ${String(activeAxis?.label || '').toLowerCase()} · ${activeDimension.label}${
    activeMetric ? ` · ${activeMetric.label}` : ''
  }`;

  const rangeOptions = useMemo(
    () => (isRangeAxis ? rows.map((row) => ({ key: row.key, label: row.label })) : []),
    [isRangeAxis, rows]
  );

  const filterSections = useMemo(() => {
    const dimensionSections = filterDimensions.map((dimension) => {
      const sourceItems =
        dimension.key === PRODUCT_NAME_DIMENSION.key
          ? items.filter((item) => Number(item?.pricePerGallon || 0) > 0)
          : items;
      const options = discoverDimensionOptions(sourceItems, dimension).map((option) => ({
        ...option,
        color: option.color || (dimension.key === activeDimension.key ? seriesColors[option.key] : undefined),
      }));
      return {
        key: dimension.key,
        title: dimension.label,
        options,
        visibility: dimensionVisibility[dimension.key] || {},
        onToggle: (key) =>
          setDimensionVisibility((prev) => ({
            ...prev,
            [dimension.key]: toggleVisibilityKey(prev[dimension.key] || {}, key),
          })),
        onShowAll: () =>
          setDimensionVisibility((prev) => ({
            ...prev,
            [dimension.key]: visibilityMapFromOptions(options, true),
          })),
        onHideAll: () =>
          setDimensionVisibility((prev) => ({
            ...prev,
            [dimension.key]: visibilityMapFromOptions(options, false),
          })),
      };
    });

    if (isAmountAxis) {
      return [
        {
          key: 'amountRangeConfig',
          title: 'Rangos de monto',
          type: 'numericRange',
          hint: 'Límites y tamaño de rangos en millones de CLP (c/IVA).',
          fields: AMOUNT_RANGE_FIELDS,
          config: amountRangeDraft,
          onChange: (patch) => setAmountRangeDraft((prev) => ({ ...prev, ...patch })),
          onCommit: () => {
            setAmountRangeDraft((draft) => {
              const next = normalizeAmountRangeConfig(draft);
              setAmountRangeConfig(next);
              setRangeVisibility({});
              return next;
            });
          },
        },
        ...dimensionSections,
        {
          key: 'amountRanges',
          title: 'Mostrar rangos',
          options: rangeOptions,
          visibility: rangeVisibility,
          onToggle: (key) => setRangeVisibility((prev) => toggleVisibilityKey(prev, key)),
          onShowAll: () => setRangeVisibility(visibilityMapFromOptions(rangeOptions, true)),
          onHideAll: () => setRangeVisibility(visibilityMapFromOptions(rangeOptions, false)),
        },
      ];
    }

    if (isGallonPriceAxis) {
      const productSection = dimensionSections.find((section) => section.key === PRODUCT_NAME_DIMENSION.key);
      const otherSections = dimensionSections.filter((section) => section.key !== PRODUCT_NAME_DIMENSION.key);
      return [
        {
          key: 'gallonPriceRangeConfig',
          title: 'Rangos precio/galón',
          type: 'numericRange',
          hint: 'Precio neto T-COAT por galón equivalente (Tineta 16 L = 4 gal, Tineta 20 L = 5 gal). Valores en miles de CLP.',
          fields: GALLON_PRICE_RANGE_FIELDS,
          config: gallonPriceDraft,
          onChange: (patch) => setGallonPriceDraft((prev) => ({ ...prev, ...patch })),
          onCommit: () => {
            setGallonPriceDraft((draft) => {
              const next = normalizeGallonPriceRangeConfig(draft);
              setGallonPriceConfig(next);
              setRangeVisibility({});
              return next;
            });
          },
        },
        ...(productSection ? [productSection] : []),
        ...otherSections,
        {
          key: 'gallonPriceRanges',
          title: 'Mostrar rangos',
          options: rangeOptions,
          visibility: rangeVisibility,
          onToggle: (key) => setRangeVisibility((prev) => toggleVisibilityKey(prev, key)),
          onShowAll: () => setRangeVisibility(visibilityMapFromOptions(rangeOptions, true)),
          onHideAll: () => setRangeVisibility(visibilityMapFromOptions(rangeOptions, false)),
        },
      ];
    }

    return dimensionSections;
  }, [
    filterDimensions,
    items,
    dimensionVisibility,
    activeDimension.key,
    seriesColors,
    isAmountAxis,
    isGallonPriceAxis,
    amountRangeDraft,
    gallonPriceDraft,
    rangeOptions,
    rangeVisibility,
  ]);

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '10px',
        overflow: 'visible',
        margin: '10px 0 12px',
        background: panel,
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '7px 10px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          borderRadius: '10px 10px 0 0',
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <ChartOptionsMenu
            axisModes={modes}
            axisKey={axisKey}
            onAxisKeyChange={setAxisKey}
            dimensions={dimensions}
            stackKey={stackKey}
            onStackKeyChange={setStackKey}
            metrics={metrics}
            metricKey={resolvedMetricKey}
            onMetricKeyChange={setMetricKey}
            filterSections={filterSections}
          />
          <h3 style={{ margin: 0, fontSize: '13px', color: text, fontWeight: 700, lineHeight: 1.2 }}>
            {chartTitle}
          </h3>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span style={{ fontSize: '11px', color: muted, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {formatChartMetricValue(total, resolvedMetricKey)} {resolvedTotalLabel}
          </span>
          <ChartPresetsMenu
            presets={filterChartPresets(chartPresets, {
              axisModes: modes,
              dimensions,
              metrics,
            })}
            axisKey={axisKey}
            stackKey={stackKey}
            metricKey={resolvedMetricKey}
            onApply={(preset) => {
              if (preset?.axisKey) setAxisKey(preset.axisKey);
              if (preset?.stackKey) setStackKey(preset.stackKey);
              if (preset?.metricKey) setMetricKey(preset.metricKey);
              setHoverInfo(null);
            }}
          />
        </div>
      </div>
      {!hasAnyData ? (
        <div style={{ padding: '18px 12px', color: muted, fontSize: '12px', textAlign: 'center' }}>
          {emptyMessage}
        </div>
      ) : (
        <>
          {seriesPresent.length > 0 ? (
            <ChartSeriesLegend series={seriesPresent} colors={seriesColors} />
          ) : null}
          <div style={{ overflowX: isRangeAxis ? 'auto' : 'visible', padding: '12px 10px 8px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: isRangeAxis ? '8px' : '4px',
                width: isRangeAxis ? undefined : '100%',
                minWidth: isRangeAxis ? Math.max(280, visibleRows.length * 52) : undefined,
                height: `${CHART_PLOT_HEIGHT_PX}px`,
              }}
            >
              {visibleRows.map((item) => {
                const heightPct = item.total > 0 ? (item.total / maxCount) * 100 : 0;
                const seriesRows = seriesPresent
                  .filter((series) => Number(item.bySeries[series] || 0) > 0)
                  .map((series) => ({
                    series,
                    value: Number(item.bySeries[series] || 0),
                    color: seriesColors[series],
                  }));
                const showHover = (event, focusSeries = null) => {
                  setHoverInfo({
                    x: event.clientX,
                    y: event.clientY,
                    label: item.label,
                    total: item.total,
                    chartTotal: total,
                    metricKey: resolvedMetricKey,
                    focusSeries,
                    rows: seriesRows,
                  });
                };

                return (
                  <div
                    key={item.key}
                    onMouseEnter={(event) => showHover(event)}
                    onMouseMove={(event) => showHover(event)}
                    onMouseLeave={() => setHoverInfo(null)}
                    style={{
                      flex: '1 1 0',
                      minWidth: isRangeAxis ? '40px' : 0,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      minHeight: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: text,
                        lineHeight: 1,
                        opacity: item.total > 0 ? 1 : 0.35,
                        flexShrink: 0,
                      }}
                    >
                      {formatChartMetricValue(item.total, resolvedMetricKey)}
                    </span>
                    <div
                      style={{
                        flex: '1 1 0',
                        minHeight: 0,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                      }}
                    >
                      <div
                        style={{
                          width: '70%',
                          maxWidth: '34px',
                          height: `${heightPct}%`,
                          minHeight: item.total > 0 ? '3px' : 0,
                          flexShrink: 0,
                          display: 'flex',
                          flexDirection: 'column-reverse',
                          overflow: 'hidden',
                          borderRadius: '5px 5px 2px 2px',
                          background:
                            item.total > 0
                              ? 'transparent'
                              : `color-mix(in srgb, ${muted} 18%, ${panel})`,
                        }}
                      >
                        {seriesPresent.map((series) => {
                          const count = Number(item.bySeries[series] || 0);
                          if (count <= 0) return null;
                          return (
                            <div
                              key={series}
                              style={{
                                flex: count,
                                minHeight: 2,
                                background: seriesColors[series],
                                width: '100%',
                              }}
                              onMouseEnter={(event) => {
                                event.stopPropagation();
                                showHover(event, series);
                              }}
                              onMouseMove={(event) => {
                                event.stopPropagation();
                                showHover(event, series);
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: '9px',
                        fontWeight: 600,
                        color: muted,
                        textAlign: 'center',
                        lineHeight: 1.1,
                        maxWidth: '100%',
                        whiteSpace: isRangeAxis ? 'normal' : 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: isRangeAxis ? '-webkit-box' : undefined,
                        WebkitLineClamp: isRangeAxis ? 2 : undefined,
                        WebkitBoxOrient: isRangeAxis ? 'vertical' : undefined,
                        wordBreak: isRangeAxis ? 'break-word' : undefined,
                        flexShrink: 0,
                      }}
                    >
                      {item.shortLabel || item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <StackedChartHoverTooltip ui={ui} info={hoverInfo} />
        </>
      )}
    </div>
  );
};

export default RegionSegmentStackedChart;
